import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import XLR8Logo from '../../components/XLR8Logo'
import LoadError from '../../components/LoadError'
import { getCustomers, quickAssess } from '../../api'
import { customerListFrom, loadErrorMessage } from '../../utils/loadState'
import CustomerAvatar from '../../components/CustomerAvatar'
import './CustomerList.css'

export default function CustomerList() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  // Customer ids with a contract on file (GET /api/context `contracts_on_file`, already
  // scoped to the caller's customers). Empty when the server didn't say -- no badge then.
  const [withContract, setWithContract] = useState(new Set())
  const [assessments, setAssessments] = useState({}) // keyed by customer_id
  // Customer ids whose assessment request failed -> "unknown", never "0 tables / No Data".
  const [assessFailed, setAssessFailed] = useState(new Set())
  const [assessDone, setAssessDone] = useState(false)
  const [loading, setLoading] = useState(true)
  // A failed customer load is an error with a Retry, never "No customers yet" (item 201).
  const [loadError, setLoadError] = useState(null)
  const [search, setSearch] = useState('')

  async function load() {
    setLoading(true)
    setLoadError(null)
    setAssessDone(false)
    let list
    try {
      const data = await getCustomers()
      list = customerListFrom(data)
      setCustomers(list)
      setWithContract(new Set(data.contracts_on_file || []))
    } catch (err) {
      console.error('Failed to load customers:', err)
      setCustomers([])
      setLoadError(loadErrorMessage(err))
      setLoading(false)
      return
    }
    setLoading(false) // Show customers immediately

    // Load assessments in background — don't block rendering
    const assessMap = {}
    const failed = new Set()
    await Promise.allSettled(
      list.map(async (c) => {
        try {
          const a = await quickAssess(c.customer_id)
          if (a) assessMap[c.customer_id] = a
          else failed.add(c.customer_id)
        } catch {
          failed.add(c.customer_id)
        }
      })
    )
    setAssessments(assessMap)
    setAssessFailed(failed)
    setAssessDone(true)
  }

  useEffect(() => { load() }, [])

  const filtered = customers.filter(c => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.industry || '').toLowerCase().includes(q) ||
      (c.customer_id || '').toLowerCase().includes(q)
    )
  })



  function getSystemLabel(c) {
    if (c.systems && c.systems.length > 0) {
      const primary = c.systems.find(s => s.is_primary) || c.systems[0]
      return primary.product_name || primary.vendor_name || ''
    }
    return c.hcm_product || c.hcm_vendor || ''
  }

  const breadcrumbs = [{ label: 'Customers' }]
  const navActions = (
    <>
      <button className="nav-btn primary" onClick={() => navigate('/customers/new')}>+ New Customer</button>
      
    </>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="customer-list-page">
        {/* Header */}
        <div className="cl-header">
          <div>
            <h1 className="cl-title">Customers</h1>
            <p className="cl-subtitle">{loadError ? 'Customers could not be loaded' : `${customers.length} customer${customers.length !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="cl-controls">
            <input
              className="cl-search"
              type="text"
              placeholder="Search customers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="cl-loading">
            <div className="cl-loading-spinner" />
            <p>Loading customers…</p>
          </div>
        ) : loadError ? (
          <LoadError what="customers" error={loadError} onRetry={load} />
        ) : filtered.length === 0 ? (
          <div className="cl-empty">
            {customers.length === 0 ? (
              <>
                <div className="cl-empty-icon">👋</div>
                <h3>No customers yet</h3>
                <p>Create your first customer to get started with Sempirical.</p>
                <button className="cl-cta" onClick={() => navigate('/customers/new')}>+ New Customer</button>
              </>
            ) : (
              <>
                <div className="cl-empty-icon">🔍</div>
                <h3>No matches</h3>
                <p>No customers match "{search}"</p>
              </>
            )}
          </div>
        ) : (
          <div className="cl-grid">
            {filtered.map(c => {
              const assess = assessments[c.customer_id]
              const empCount = c.employee_count ? c.employee_count.toLocaleString() : '—'
              const sysLabel = getSystemLabel(c)
              // No assessment (still loading, or the request failed) is unknown, not zero.
              const assessUnknown = !assess
              const tableCount = assessUnknown ? '—' : (assess.table_count || 0)
              const hasData = assess?.has_data || false
              const assessLabel = assessFailed.has(c.customer_id)
                ? 'Status unavailable'
                : assessUnknown ? (assessDone ? 'Status unavailable' : 'Checking…')
                : hasData ? 'Data Ready' : 'No Data'

              return (
                <Link
                  key={c.customer_id}
                  to={`/customers/${c.customer_id}`}
                  className="cl-card"
                >
                  <div className="cl-card-header">
                    <CustomerAvatar customerId={c.customer_id} companyName={c.company_name} website={c.website} logoBase64={c.logo_base64} size="lg" />
                    <div className="cl-card-info">
                      <div className="cl-card-name">{c.company_name}</div>
                      <div className="cl-card-meta">
                        {[c.industry, `${empCount} employees`].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </div>

                  <div className="cl-card-body">
                    {sysLabel && (
                      <div className="cl-card-system">
                        <span className="cl-card-system-dot" />
                        {sysLabel}
                      </div>
                    )}
                    <div className="cl-card-stats">
                      <div className="cl-card-stat">
                        <span className="cl-card-stat-value">{tableCount}</span>
                        <span className="cl-card-stat-label">Tables</span>
                      </div>
                      <div className="cl-card-stat">
                        <span className={`cl-card-stat-badge ${hasData ? 'ready' : 'empty'}`}>
                          {assessLabel}
                        </span>
                      </div>
                      {withContract.has(c.customer_id) && (
                        <div className="cl-card-stat">
                          <span className="cl-card-stat-badge contract" title="A contract is on file. See the customer's Contract tab.">📑 Contract on file</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="cl-card-footer">
                    <span className="cl-card-id">{c.customer_id}</span>
                    <span className="cl-card-arrow">→</span>
                  </div>
                </Link>
              )
            })}

            {/* New customer card */}
            <button className="cl-card cl-card-new" onClick={() => navigate('/customers/new')}>
              <div className="cl-new-icon">+</div>
              <div className="cl-new-text">New Customer</div>
            </button>
          </div>
        )}
      </div>
    </>
  )
}


